import { AxiosError } from 'axios';
import { UseQueryOptions, useMutation, useQuery } from 'react-query';

import * as Repo from './bookings.repo';
import * as Types from './bookings.types';

export const useCancelAllBookings = (opts?) => useMutation<any, AxiosError, string>(Repo.cancelAllBookings, opts);

export const useCancelBooking = (opts?) => useMutation(Repo.cancelBooking, opts);

export const useEditBooking = (opts?) =>
  useMutation<any, AxiosError, Types.EditBookingRequest>(
    ({ bookingId, booking }) => Repo.editBooking(bookingId, booking),
    opts,
  );

export const useGetReservation = (req: Types.GetBookingRequest, opts?) =>
  useQuery(['getBooking', req], () => Repo.getBooking(req), opts);

export const useBookReservation = (opts?) =>
  useMutation<Types.DoBookingResponse, AxiosError, Types.DoBookingRequest>(
    ({
      recaptchaToken,
      locationId,
      payment,
      hasSessions,
      trxId,
      paymentPagoFacil,
      paymentDLocal,
      bookings,
      client,
      creativeSource,
      externalReference,
      rwgToken,
    }) =>
      Repo.bookReservation(
        locationId,
        payment,
        hasSessions,
        trxId,
        paymentPagoFacil,
        paymentDLocal,
        bookings,
        client,
        creativeSource,
        rwgToken,
        externalReference,
        recaptchaToken,
      ),
    opts,
  );

export const useCheckBookingPlans = (opts?) =>
  useMutation<any, AxiosError<Types.ErrorResponse>, Types.CheckBookingRequest>(
    ({ locationId, bookings, client }) => Repo.checkBookingPlans(locationId, { bookings }, client),
    opts,
  );

export const useConfirmBooking = (
  confirmationCode: string,
  opts?: Omit<
    UseQueryOptions<Types.ConfirmBookingResponse, AxiosError, Types.ConfirmBookingResponse, [string, string]>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<Types.ConfirmBookingResponse, AxiosError, Types.ConfirmBookingResponse, [string, string]>(
    ['confirmBooking', confirmationCode],
    () => Repo.confirmBooking(confirmationCode),
    opts,
  );

export const useConfirmAllBookings = (confirmationCode) =>
  useQuery(['confirmAllBookings', { confirmationCode }], () => Repo.confirmAllBookings(confirmationCode));

export const useBookingData = (accessToken, bookingId, onSuccess?, onError?) =>
  useQuery(['getBooking', { accessToken, bookingId }], () => Repo.getBooking({ accessToken, bookingId }), {
    enabled: !!accessToken && !!bookingId,
    onSuccess,
    onError,
  });

export const useGetEditBookingData = (confirmationCode, onSuccess?, onError?) =>
  useQuery(['getEditBookingData', { confirmationCode }], () => Repo.getEditBookingData(confirmationCode), {
    onSuccess,
    onError,
  });

export const useGetReviewSummary = (locationId, reviewsv2 = false) =>
  useQuery(['getReviewSummary', { locationId }], () => Repo.getReviewSummary(locationId, reviewsv2));

export const useGetCanReview = (confirmationCode, reviewsv2 = false) =>
  useQuery(['getCanReview', { confirmationCode }], () => Repo.getCanReview(confirmationCode, reviewsv2));

export const usePatchReview = (opts?) =>
  useMutation<any, AxiosError, Types.PatchReviewRequest>(
    ({ stars, anonymous, reviewerName, confirmationCode, serviceName, comment, reviewsv2 = false, image }) =>
      Repo.patchReview({ stars, anonymous, reviewerName, confirmationCode, serviceName, comment, reviewsv2, image }),
    opts,
  );
export const useCommentReview = (opts?) =>
  useMutation<any, AxiosError, Types.PostCommentReview>(
    ({ confirmationCode, comment }) => Repo.commentReview({ confirmationCode, comment }),
    opts,
  );
